/**
 * Phase 8 T-06c / T-22 / T-26 §27 + §29.E + §29.F — Smart Match intake.
 *
 * Refactored in T-22 to consume the reusable `<AssistantIntake>` component.
 * T-26 added two more intake bodies:
 *
 *   - `<MultiJdList />`     — up to 10 JDs + `[⚙ Manual review]` toggle.
 *   - `<BulkResumeUpload />` — up to 100 files / 50 MB + parse statuses.
 *
 * 21-Apr follow-up (user feedback): the old `[Single JD | Multi JD]`
 * split was collapsed into one unified **Job description** tab. That
 * tab renders `AssistantIntake` when the user has 0 queued JDs (a
 * clean freeform composer with JD-template chips) and graduates to
 * `MultiJdList` the moment a second JD is added. On submit the page
 * auto-routes between `handleSingleSubmit` (1 JD) and `handleMultiSubmit`
 * (2+ JDs) — no tab switching required.
 *
 * The 3-tab `<JdModeTabs />` ( `Job description | Bulk resumes |
 * Refine Search` ) switches between intake bodies inside the same
 * shell. Bulk-resume uses a parallel shell that mirrors AssistantIntake
 * visually without modifying that component's layout primitives
 * (§ spec guard).
 *
 * Per-tab input is preserved across switches — Job-description draft
 * (JD list + manual-review flag) and Bulk-resume draft (metadata only,
 * no file bytes) are kept in React state at this level and persisted
 * to localStorage under `ggj.smartmatch.intake.draft.{multi|bulk}`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FolderOpen, Send } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { AgentMark } from '@shared/components/AgentMark';
import {
  AssistantIntake,
  INTAKE_GRADIENT_LIGHT,
  INTAKE_GRADIENT_DARK,
} from '@modules/agent/components/AssistantIntake';
import { INTAKE_CONFIGS } from '@modules/agent/intake.config';
import { writePipelineIntake } from './lib/matchCanvasPipeline';
import { storePendingResumeTexts } from './lib/pendingResumeTexts';
import {
  threadNameFromPrompt,
  upsertThread,
  type SmartMatchIntakeTab,
  type SmartMatchJdEntry,
  type SmartMatchResumeEntry,
  type SmartMatchThread,
} from './lib/smartMatchThreads';
import { HistorySlideIn } from './components/HistorySlideIn';
import { PreferencesModal } from './components/PreferencesModal';
import {
  JdModeTabs,
  readJdMode,
  type JdMode,
} from './components/JdModeTabs';
import {
  MultiJdList,
  type MultiJdItem,
  type MultiJdListHandle,
} from './components/MultiJdList';
import {
  BulkResumeUpload,
  type BulkResumeItem,
  type BulkResumeUploadHandle,
} from './components/BulkResumeUpload';

const DRAFT_MULTI_KEY = 'ggj.smartmatch.intake.draft.multi';
const DRAFT_BULK_KEY = 'ggj.smartmatch.intake.draft.bulk';

interface MultiDraft {
  jds: MultiJdItem[];
  manualReview: boolean;
}
interface BulkDraft {
  /** Persist metadata only — file bytes + parsed text are session-only. */
  items: Array<Omit<BulkResumeItem, 'text'>>;
  contextJd: string;
}

function newThreadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function firstNameOf(user: { name?: string | null; email?: string | null } | null): string {
  const name = (user?.name ?? '').trim();
  if (name) {
    const first = name.split(/\s+/)[0];
    if (first && first.length > 0) return first;
  }
  const email = (user?.email ?? '').trim();
  if (email) {
    const handle = email.split('@')[0];
    if (handle) return handle.charAt(0).toUpperCase() + handle.slice(1);
  }
  return 'there';
}

function scopeSourceFor(scope: string | null): 'pool' | 'shared' | 'external' {
  if (scope === 'External') return 'external';
  if (scope === 'Career page') return 'shared';
  return 'pool';
}

function readMultiDraft(): MultiDraft {
  if (typeof window === 'undefined') return { jds: [], manualReview: false };
  try {
    const raw = window.localStorage.getItem(DRAFT_MULTI_KEY);
    if (!raw) return { jds: [], manualReview: false };
    const parsed = JSON.parse(raw) as Partial<MultiDraft>;
    const jds = Array.isArray(parsed.jds)
      ? parsed.jds.filter(
          (j): j is MultiJdItem =>
            !!j &&
            typeof j === 'object' &&
            typeof (j as MultiJdItem).id === 'string' &&
            typeof (j as MultiJdItem).text === 'string',
        )
      : [];
    return { jds, manualReview: !!parsed.manualReview };
  } catch {
    return { jds: [], manualReview: false };
  }
}

function writeMultiDraft(draft: MultiDraft): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRAFT_MULTI_KEY, JSON.stringify(draft));
  } catch {
    /* quota / private mode */
  }
}

function readBulkDraft(): BulkDraft {
  if (typeof window === 'undefined') return { items: [], contextJd: '' };
  try {
    const raw = window.localStorage.getItem(DRAFT_BULK_KEY);
    if (!raw) return { items: [], contextJd: '' };
    const parsed = JSON.parse(raw) as Partial<BulkDraft>;
    const items = Array.isArray(parsed.items)
      ? parsed.items.filter(
          (it): it is Omit<BulkResumeItem, 'text'> =>
            !!it &&
            typeof it === 'object' &&
            typeof (it as BulkResumeItem).id === 'string' &&
            typeof (it as BulkResumeItem).name === 'string' &&
            typeof (it as BulkResumeItem).sizeBytes === 'number',
        )
      : [];
    return {
      items,
      contextJd: typeof parsed.contextJd === 'string' ? parsed.contextJd : '',
    };
  } catch {
    return { items: [], contextJd: '' };
  }
}

function writeBulkDraft(draft: BulkDraft): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DRAFT_BULK_KEY, JSON.stringify(draft));
  } catch {
    /* quota / private mode */
  }
}

function clearDraft(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* no-op */
  }
}

export function SmartMatchIntakePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();

  const [jdMode, setJdMode] = useState<JdMode>(() => readJdMode());
  const [fadingOut, setFadingOut] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const firstName = useMemo(() => firstNameOf(user), [user]);

  const baseCfg = useMemo(() => {
    const row = INTAKE_CONFIGS.find((c) => c.route === '/smart-match/new');
    if (!row) throw new Error('intake config for /smart-match/new is missing');
    return row;
  }, []);

  // §29.E Multi-JD draft state
  const [multiDraft, setMultiDraftState] = useState<MultiDraft>(() => readMultiDraft());
  const onMultiChange = useCallback(
    (jds: MultiJdItem[], manualReview: boolean) => {
      const next = { jds, manualReview };
      setMultiDraftState(next);
      writeMultiDraft(next);
    },
    [],
  );
  const multiHandleRef = useRef<MultiJdListHandle | null>(null);

  // §29.F Bulk-resumes draft state
  const [bulkDraft, setBulkDraftState] = useState<BulkDraft>(() => readBulkDraft());
  const onBulkChange = useCallback(
    (items: BulkResumeItem[], contextJd: string) => {
      // Strip the parsed-text body before persisting (metadata only).
      const stripped: Array<Omit<BulkResumeItem, 'text'>> = items.map(
        ({ text: _text, ...rest }) => rest,
      );
      const next = { items: stripped, contextJd };
      setBulkDraftState({ items: stripped, contextJd });
      writeBulkDraft(next);
    },
    [],
  );
  const bulkHandleRef = useRef<BulkResumeUploadHandle | null>(null);

  // 21-Apr unification: the Job-description tab is the new "single" path
  // — its placeholder matches the old Single-JD copy. Refine-search falls
  // back to the registry placeholder (candidate-centric).
  const placeholder =
    jdMode === 'job'
      ? 'Paste your full job description here…'
      : baseCfg.placeholder;

  const seededPrompt = params.get('q') ?? '';

  const commitThreadAndNavigate = useCallback(
    (thread: SmartMatchThread) => {
      upsertThread(thread);
      writePipelineIntake(thread.id, {
        prompt: thread.prompt,
        source:
          thread.scopeId === 'external'
            ? 'external'
            : thread.scopeId === 'career'
              ? 'shared'
              : 'pool',
        createdAt: thread.createdAt,
      });
      setFadingOut(true);
      window.setTimeout(() => navigate(`/smart-match/${thread.id}`), 180);
    },
    [navigate],
  );

  // Job-description (single) / Refine-search submit path — unchanged
  // thread shape from T-22. Multi-JD submissions go through
  // `handleMultiSubmit` below and persist with `mode: 'multi'`.
  const handleSingleSubmit = useCallback(
    (text: string, scope: string | null) => {
      if (fadingOut) return;
      const scopeId = newThreadId();
      const now = Date.now();
      const intakeTab: SmartMatchIntakeTab =
        jdMode === 'job' ? 'job-description' : 'refine-search';
      const thread: SmartMatchThread = {
        id: scopeId,
        ...(user?.id ? { createdByUserId: user.id } : {}),
        name: threadNameFromPrompt(text),
        prompt: text,
        intakeTab,
        scopeId: scope === 'External' ? 'external' : scope === 'Career page' ? 'career' : 'org',
        collection: null,
        candidateCount: 0,
        createdAt: now,
        updatedAt: now,
        stage: 'clarify',
        clarificationRoundCount: 0,
        mode: 'single',
      };
      commitThreadAndNavigate(thread);
    },
    [fadingOut, jdMode, commitThreadAndNavigate, user?.id],
  );

  // §29.E Multi-JD submit — folds the JD list + manual-review flag onto
  // the thread and uses a concatenated summary for the `prompt` field so
  // the history list still reads usefully.
  const handleMultiSubmit = useCallback(() => {
    if (fadingOut) return;
    const handle = multiHandleRef.current;
    const jds = handle?.getJds() ?? multiDraft.jds;
    if (jds.length === 0) return;
    const manualReview = handle?.isManualReview() ?? multiDraft.manualReview;
    const scopeId = newThreadId();
    const now = Date.now();
    const firstPreview = jds[0]?.preview ?? '';
    const promptSummary =
      jds.length === 1
        ? firstPreview
        : `Multi-JD (${jds.length}): ${firstPreview}…`;
    const jdList: SmartMatchJdEntry[] = jds.map((j) => ({
      id: j.id,
      title: j.title,
      text: j.text,
    }));
    const thread: SmartMatchThread = {
      id: scopeId,
      ...(user?.id ? { createdByUserId: user.id } : {}),
      name: threadNameFromPrompt(`Multi-JD • ${firstPreview}`),
      prompt: promptSummary,
      intakeTab: 'job-description',
      scopeId: 'org',
      collection: null,
      candidateCount: 0,
      createdAt: now,
      updatedAt: now,
      stage: 'clarify',
      clarificationRoundCount: 0,
      mode: 'multi',
      manualReview,
      jdList,
      activeJdTab: 'combined',
    };
    clearDraft(DRAFT_MULTI_KEY);
    commitThreadAndNavigate(thread);
  }, [fadingOut, multiDraft, commitThreadAndNavigate, user?.id]);

  // §29.F Bulk-resumes submit — folds the file list + Role/JD context
  // onto the thread. Items are persisted as metadata only (file bytes
  // are never in localStorage).
  const handleBulkSubmit = useCallback(() => {
    if (fadingOut) return;
    const handle = bulkHandleRef.current;
    const items = handle?.getItems() ?? [];
    const contextJd = handle?.getContextJd() ?? bulkDraft.contextJd;
    if (items.length === 0) return;
    const scopeId = newThreadId();
    const now = Date.now();
    const leadName = items[0]?.name ?? 'resumes';
    const promptSummary = `Bulk resumes (${items.length}): ${leadName}…`;
    const resumeList: SmartMatchResumeEntry[] = items.map((it) => ({
      id: it.id,
      name: it.name,
      sizeBytes: it.sizeBytes,
      parseStatus: it.status,
      parseError: it.error,
    }));
    // 21-Apr-26 — persist the parsed resume bodies into a sessionStorage
    // bridge so the canvas can actually score the uploads. The thread
    // itself only stores metadata (localStorage budget). See
    // `pendingResumeTexts.ts` for the rationale.
    const parsedItems = items.filter((it) => it.status === 'parsed' && it.text);
    storePendingResumeTexts(
      scopeId,
      parsedItems.map((it) => ({
        id: it.id,
        name: it.name,
        text: it.text ?? '',
      })),
    );
    if (import.meta.env.DEV) {
      console.info('[SmartMatchIntake] bulk submit', {
        scopeId,
        totalItems: items.length,
        parsedCount: parsedItems.length,
        statusCounts: items.reduce<Record<string, number>>((acc, it) => {
          acc[it.status] = (acc[it.status] ?? 0) + 1;
          return acc;
        }, {}),
      });
    }
    const thread: SmartMatchThread = {
      id: scopeId,
      ...(user?.id ? { createdByUserId: user.id } : {}),
      name: threadNameFromPrompt(`Bulk resumes • ${leadName}`),
      prompt: promptSummary,
      intakeTab: 'job-description',
      scopeId: 'org',
      collection: null,
      candidateCount: 0,
      createdAt: now,
      updatedAt: now,
      stage: 'clarify',
      clarificationRoundCount: 0,
      mode: 'bulk',
      resumeList,
      bulkContextJd: contextJd,
      activeBulkTab: 'high',
    };
    clearDraft(DRAFT_BULK_KEY);
    commitThreadAndNavigate(thread);
  }, [fadingOut, bulkDraft.contextJd, commitThreadAndNavigate, user?.id]);

  // Auto-submit if we were seeded from a shared URL (Job / Refine only).
  useEffect(() => {
    if (seededPrompt && !fadingOut && (jdMode === 'job' || jdMode === 'refine')) {
      const t = window.setTimeout(
        () => handleSingleSubmit(seededPrompt.trim(), 'Org database'),
        50,
      );
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [seededPrompt, fadingOut, jdMode, handleSingleSubmit]);

  const renderTopStart = useCallback(
    () => (
      // Post user-feedback (21-Apr) — History button now has a solid
      // elevated background + stronger text colour so it stays legible
      // over the ambient pastel gradient. Previously the transparent
      // pill washed out against the peach/lavender blend.
      <button
        type="button"
        onClick={() => setHistoryOpen(true)}
        className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-2.5 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-primary)',
          background: 'var(--vscode-bg-elevated)',
          boxShadow: '0 1px 2px rgba(17, 24, 39, 0.04)',
        }}
        aria-label="Open search history"
      >
        <FolderOpen className="h-3.5 w-3.5" aria-hidden />
        History
      </button>
    ),
    [],
  );

  const renderTopEnd = useCallback(
    () => (
      <div className="flex items-center gap-2">
        <JdModeTabs
          value={jdMode}
          onChange={setJdMode}
          onPreferences={() => setPreferencesOpen(true)}
        />
      </div>
    ),
    [jdMode],
  );

  const greetingText = baseCfg.greeting.replace('{firstName}', firstName);

  // 21-Apr unification — the Job-description tab shows the freeform
  // `AssistantIntake` only when there are NO queued JDs. Once the user
  // has drafted 1+ JDs in the multi list, the tab swaps to
  // `MultiJdList`. This collapses the old Single/Multi split without
  // losing the fast-path composer for single-JD users.
  const jobUsingMultiList = jdMode === 'job' && multiDraft.jds.length > 0;

  // §29.E + §29.F — Multi-JD list body and Bulk-resumes body both
  // render inside a parallel shell that mirrors `<AssistantIntake>`'s
  // greeting + card chrome without modifying that component's layout
  // primitives.
  if (jobUsingMultiList || jdMode === 'bulk') {
    const canSubmit = jobUsingMultiList
      ? multiDraft.jds.length > 0
      : bulkDraft.items.length > 0;
    // Submit copy: 1 JD reads like the old Single-JD affordance,
    // 2+ JDs reads like the old Multi-JD affordance, bulk stays as
    // "Start scoring" (matches existing resume-dropzone CTA).
    const submitLabel = jobUsingMultiList
      ? multiDraft.jds.length === 1
        ? 'Start sourcing'
        : `Start sourcing (${multiDraft.jds.length} JDs)`
      : 'Start scoring';
    const scopeLabel = jobUsingMultiList
      ? 'Scoring over: org pool'
      : 'Scoring over: uploaded files only';

    return (
      // Post user-feedback (21-Apr, 3rd round) — cancel AppLayout's
      // `<main>` padding (`p-4 lg:p-6`) with negative margin so the
      // gradient goes edge-to-edge and NO white strips are visible
      // around the intake surface. Inner sections still get their
      // own padding via the children below.
      <div
        className="relative -m-4 flex min-h-full flex-1 flex-col overflow-hidden transition-opacity lg:-m-6"
        style={{
          opacity: fadingOut ? 0 : 1,
          transitionDuration: '180ms',
          minHeight: 'calc(100dvh - 56px)',
        }}
        data-ggj-smart-match-intake
      >
        {/* Ambient Wrangle-style pastel gradient — full surface */}
        <div
          aria-hidden
          className="ggj-intake-gradient ggj-intake-gradient--light pointer-events-none absolute inset-0"
          style={{ background: INTAKE_GRADIENT_LIGHT }}
        />
        <div
          aria-hidden
          className="ggj-intake-gradient ggj-intake-gradient--dark pointer-events-none absolute inset-0"
          style={{ background: INTAKE_GRADIENT_DARK }}
        />

        <div className="relative flex items-start justify-between px-6 pt-6">
          <div>{renderTopStart()}</div>
          <div>{renderTopEnd()}</div>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-8">
          <div className="flex w-full max-w-[640px] flex-col items-center">
            <div className="mb-6 flex items-center gap-2">
              <AgentMark className="h-5 w-5" withHalo ariaLabel={null} />
              <h1
                className="text-center text-[22px] font-semibold leading-tight"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                {greetingText}
              </h1>
            </div>

            <div
              className="w-full rounded-[12px] border p-4"
              style={{
                background: 'var(--vscode-bg-elevated)',
                borderColor: 'var(--vscode-border-primary)',
              }}
            >
              {jobUsingMultiList ? (
                <MultiJdList
                  initial={multiDraft.jds}
                  initialManualReview={multiDraft.manualReview}
                  onChange={onMultiChange}
                  handleRef={multiHandleRef}
                />
              ) : (
                <BulkResumeUpload
                  initial={bulkDraft.items}
                  initialContextJd={bulkDraft.contextJd}
                  onChange={onBulkChange}
                  handleRef={bulkHandleRef}
                />
              )}

              {/* Footer — scope pill + Submit */}
              <div
                className="mt-4 flex items-center justify-between gap-2 border-t pt-3"
                style={{ borderColor: 'var(--vscode-border-primary)' }}
              >
                <span
                  className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px]"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-tertiary)',
                  }}
                >
                  {scopeLabel}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (!jobUsingMultiList) {
                      handleBulkSubmit();
                      return;
                    }
                    // 21-Apr unification: when the user has stayed at 1 JD
                    // inside the multi list, re-enter the single path so
                    // downstream flows (thread.mode === 'single',
                    // scoreJobAgainstPool scope) behave as if they came
                    // from the old Single-JD tab. 2+ JDs take the
                    // existing multi path.
                    const handle = multiHandleRef.current;
                    const jds = handle?.getJds() ?? multiDraft.jds;
                    if (jds.length === 1 && jds[0]) {
                      handleSingleSubmit(jds[0].text, 'Org database');
                    } else {
                      handleMultiSubmit();
                    }
                  }}
                  disabled={!canSubmit}
                  className="inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-emerald-500 px-3 text-[12px] font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" aria-hidden />
                  {submitLabel}
                </button>
              </div>
            </div>
          </div>
        </div>

        <HistorySlideIn
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onNewSearch={() => setHistoryOpen(false)}
          onOpenThread={(id) => {
            setHistoryOpen(false);
            setFadingOut(true);
            window.setTimeout(() => navigate(`/smart-match/${id}`), 180);
          }}
        />
        <PreferencesModal open={preferencesOpen} onClose={() => setPreferencesOpen(false)} />
      </div>
    );
  }

  // Job-description (0 queued JDs) / Refine Search — reuse the shared
  // AssistantIntake component. The moment the user clicks
  // [+ Add another JD] the draft graduates to `MultiJdList` above.
  return (
    // Full-bleed: cancel AppLayout's `<main>` padding with negative
    // margin so the gradient covers the entire surface edge-to-edge.
    <div
      className="relative -m-4 flex min-h-full flex-1 flex-col overflow-hidden transition-opacity lg:-m-6"
      style={{
        opacity: fadingOut ? 0 : 1,
        transitionDuration: '180ms',
        minHeight: 'calc(100dvh - 56px)',
      }}
      data-ggj-smart-match-intake
    >
      <AssistantIntake
        // Re-key on tab flip so Job→Refine clears the textarea cleanly.
        key={jdMode}
        greeting={baseCfg.greeting}
        placeholder={placeholder}
        scopePill={baseCfg.scopePill}
        suggestions={baseCfg.suggestions}
        beta={baseCfg.beta}
        entityPlural={baseCfg.entityPlural}
        firstName={firstName}
        gradient="intake"
        onSubmit={handleSingleSubmit}
        renderTopStart={renderTopStart}
        renderTopEnd={renderTopEnd}
        // 21-Apr unification — only on the Job-description tab.
        // Graduates the freeform composer into the multi-JD list while
        // preserving whatever the user has already typed (carried across
        // as the first queued JD).
        addAnotherAction={
          jdMode === 'job'
            ? {
                label: '+ Add JD',
                disabledWhenEmpty: false,
                uploadLabel: 'Upload JDs / resumes',
                uploadAccept: '.txt,.md,.pdf,.doc,.docx',
                onClick: (currentText) => {
                  const seedJds: MultiJdItem[] = currentText
                    ? [
                        {
                          id: newThreadId(),
                          title: 'JD 1',
                          text: currentText,
                          preview:
                            currentText.length <= 140
                              ? currentText
                              : `${currentText.slice(0, 139)}…`,
                        },
                      ]
                    : [];
                  const next = { jds: seedJds, manualReview: multiDraft.manualReview };
                  setMultiDraftState(next);
                  writeMultiDraft(next);
                },
                /*
                 * 21-Apr follow-up — file-upload companion to the paste
                 * pill. Reads each file's text (mirrors the same
                 * strategy as <MultiJdList>.handleFiles: synchronous
                 * text for .txt/.md, deferred server-side parse
                 * placeholder for .pdf/.docx). File names become JD
                 * titles; bodies seed the MultiJdList so the user can
                 * rename + start sourcing immediately.
                 *
                 * The same input is reused for resumes — if the user
                 * uploads resumes here, they'll still land in MultiJdList
                 * as "JDs"; we surface a toast advising that the Bulk
                 * resumes tab is the right home for resume batches.
                 */
                onUploadFiles: async (files) => {
                  const parsed: MultiJdItem[] = [];
                  for (const f of files) {
                    const name = f.name;
                    const ext = name.split('.').pop()?.toLowerCase() ?? '';
                    let text = '';
                    try {
                      if (ext === 'txt' || ext === 'md' || f.type.startsWith('text/')) {
                        text = await f.text();
                      } else {
                        text = `(Uploaded ${name} — full text will be parsed at scoring time.)`;
                      }
                    } catch {
                      text = `(Failed to read ${name} client-side.)`;
                    }
                    parsed.push({
                      id: newThreadId(),
                      title: name,
                      text,
                      preview:
                        text.length <= 140 ? text : `${text.slice(0, 139)}…`,
                    });
                  }
                  if (parsed.length === 0) return;
                  const next = {
                    jds: [...multiDraft.jds, ...parsed].slice(0, 10),
                    manualReview: multiDraft.manualReview,
                  };
                  setMultiDraftState(next);
                  writeMultiDraft(next);
                },
              }
            : undefined
        }
      />

      <HistorySlideIn
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onNewSearch={() => setHistoryOpen(false)}
        onOpenThread={(id) => {
          setHistoryOpen(false);
          setFadingOut(true);
          window.setTimeout(() => navigate(`/smart-match/${id}`), 180);
        }}
      />

      <PreferencesModal open={preferencesOpen} onClose={() => setPreferencesOpen(false)} />
    </div>
  );
}
